import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useGameStore } from '../../store/gameStore';
import { useRoomStore, clearGameCache } from '../../store/roomStore';
import { BoardRenderer } from '../../pixi/BoardRenderer';
import { CHARACTERS, IDENTITIES, REVERSE_CARDS, FATE_CARDS, BOARD_CELLS } from '../../data';
import { applyReverseCard } from '../../logic/gameEngine';
import { getCharWalkPath } from '../../data/assets';
import type { BoardCell } from '../../types';
import styles from './GameBoard.module.css';
import GameHeader from '../GameHeader/GameHeader';
import DiceRoller from '../DiceRoller/DiceRoller';
import CardHand from '../CardHand/CardHand';
import BuyDialog from '../BuyDialog/BuyDialog';
import EventModal from '../EventModal/EventModal';
import IdentityReveal from '../IdentityReveal/IdentityReveal';
import GameOverOverlay from './GameOverOverlay';
import PlayerList from '../PlayerList/PlayerList';
import Broadcast from '../Broadcast/Broadcast';
import Tooltip from '../Tooltip/Tooltip';
import ToastStack, { addToast, setSuppressSuccessToast } from '../Toast/ToastStack';
import ReverseShop from '../ReverseShop/ReverseShop';
import BankruptcyLiquidation from '../BankruptcyLiquidation/BankruptcyLiquidation';
import FatePreview from '../FatePreview/FatePreview';
import DiceChoice from '../DiceChoice/DiceChoice';
import OwlPreview from '../OwlPreview/OwlPreview';
import UpgradeOffer from '../UpgradeOffer/UpgradeOffer';
import JailDialog from '../JailDialog/JailDialog';
import { useMobileDetect, requestFullscreen, isFullscreen, isFullscreenSupported, useFullscreenState, isHarmonyOS } from '../../hooks/useMobileDetect';

interface CellHoverInfo {
  cellId: number;
  cell: BoardCell;
  x: number;
  y: number;
}

export default function GameBoard() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<BoardRenderer | null>(null);
  const [ready, setReady] = useState(false);
  const [hoveredCell, setHoveredCell] = useState<CellHoverInfo | null>(null);
  const [moveSteps, setMoveSteps] = useState(0);

  const {
    board, players, properties, currentPlayerIndex, turnPhase,
    diceResult, phase, winnerId, turnNumber, roundNumber, logs,
    broadcast, activeGlobalEvent, comebackNotification, landChangeNotification,
    showBankruptcyLiquidation, pendingBankruptPlayerId,
    setTurnPhase, rollDiceAction, movePlayer, nextTurn, nextPlayer,
    buyProperty, upgradeProperty, payRent, drawFateCard, payTax, sendToJail,
    deductMoney, addMoney, addLog, setDiceResult,
    checkBankruptcy, handleBankruptcy, checkComeback,
    setBroadcast, clearBroadcast,
    startBankruptcyLiquidation, cancelBankruptcyLiquidation, confirmBankruptcy, sellPropertyForLiquidation,
    _diceOptionHigh, _diceOptionLow, chooseDiceSteps,
  } = useGameStore();
  const getState = useGameStore.getState;

  // ====== 联机模式信息 ======
  const roomMode = useRoomStore(s => s.mode);
  const roomId = useRoomStore(s => s.roomId);
  const myPlayerName = useRoomStore(s => s.myPlayerName);
  const isRoomHost = useRoomStore(s => s._isHost);
  const isOnline = roomMode === 'online';
  // 🔧 联机状态 ref（避免 finishTurn 等回调中的闭包过期）
  const isOnlineRef = useRef(isOnline);
  isOnlineRef.current = isOnline;

  const currentPlayer = players[currentPlayerIndex];
  // 在线模式：始终获取本地玩家（用于显示自己独立的手牌）
  const localPlayer = useMemo(() =>
    isOnline ? players.find(p => p.name.trim().toLowerCase() === (myPlayerName || '').trim().toLowerCase()) : null,
    [isOnline, players, myPlayerName]
  );

  // ====== 回合判断 ======
  // 在线模式：只有当前玩家是自己时才能操作（Bot 回合由房主驱动）
  const isMyTurn = !isOnline || (currentPlayer?.name.trim().toLowerCase() === (myPlayerName || '').trim().toLowerCase() && !currentPlayer?.isBot);
  // 在线模式：只有房主负责执行 Bot 回合（避免所有客户端并行随机数导致反同步）
  const isResponsibleForBot = !isOnline || isRoomHost;
  const [showBuyDialog, setShowBuyDialog] = useState(false);
  const [showReverseShop, setShowReverseShop] = useState(false);
  const [showEventModal, setShowEventModal] = useState<{ type: string; data: any } | null>(null);
  const [showIdentityReveal, setShowIdentityReveal] = useState(false);
  const [autoPlayBot, setAutoPlayBot] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [currentStepCell, setCurrentStepCell] = useState('');
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [playerListCollapsed, setPlayerListCollapsed] = useState(false);
  const [portraitPlayerListCollapsed, setPortraitPlayerListCollapsed] = useState(false);
  const [showOwlPreview, setShowOwlPreview] = useState(false);
  const [owlCellInfo, setOwlCellInfo] = useState<{ cellId: number; cellName: string; cellPrice: number } | null>(null);
  const [upgradeOffer, setUpgradeOffer] = useState<{ cellId: number; cell: BoardCell; prop: any } | null>(null);
  const [jailChoice, setJailChoice] = useState(false);
  const [selectedCell, setSelectedCell] = useState<number | null>(null);
  const prevTurnPhaseRef = useRef(turnPhase);

  const lastKnownPositionsRef = useRef<Record<string, number>>({});
  const initialSyncDoneRef = useRef(false);
  const isAnimatingRef = useRef(false);  // 动画进行中标记（在 render 和 dialog effect 之前声明）

  // ====== 掷骰子倒计时（30秒） ======
  const [diceCountdown, setDiceCountdown] = useState(0);
  const countdownIntervalRef = useRef<number>(0);
  const autoRollTimeoutRef = useRef<number>(0);

  const clearCountdownTimers = useCallback(() => {
    if (countdownIntervalRef.current) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = 0; }
    if (autoRollTimeoutRef.current) { clearTimeout(autoRollTimeoutRef.current); autoRollTimeoutRef.current = 0; }
  }, []);

  // ====== 移动端检测 & 全屏状态 ======
  const { isMobile, isPortrait } = useMobileDetect();
  const isFullscreenActive = useFullscreenState();
  const isFullscreenPortrait = isFullscreenActive && isPortrait;
  const [orientationDismissed, setOrientationDismissed] = useState(false);
  const [mobilePlayerListOpen, setMobilePlayerListOpen] = useState(false);
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState<'fullscreen' | 'ios-homescreen' | 'harmonyos-homescreen' | null>(null);
  const [orientationLock, setOrientationLock] = useState<string | null>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // ====== 监听来自 App.tsx 的退出请求（浏览器返回手势 / 物理返回键） ======
  useEffect(() => {
    const handler = () => setShowExitConfirm(true);
    window.addEventListener('game:exit-request', handler);
    return () => window.removeEventListener('game:exit-request', handler);
  }, []);

  // ====== 退出游戏 ======
  const handleExitGame = useCallback(() => {
    setShowExitConfirm(false);
    // 清除浏览器缓存，避免下次打开页面自动重连
    clearGameCache();
    // 联机模式：断开连接并重置状态（本地模式无需）
    if (isOnline) useRoomStore.getState().reset();
    window.location.replace('/');
  }, [isOnline]);

  // ====== 绝境反击弹窗监听 ======
  useEffect(() => {
    if (!comebackNotification) return;
    // 仅当前玩家是人类时才弹窗（Bot的绝境反击记录到日志即可）
    const cp = getState().players[getState().currentPlayerIndex];
    if (!cp || cp.isBot) return;
    setShowEventModal({ type: 'comeback', data: comebackNotification });
    // 清除通知（避免重复弹窗）
    getState().comebackNotification = null;
  }, [comebackNotification]);

  // ====== 地产变动弹窗监听（土地丢失/降级 → 弹窗提示 → 确认后闪烁格子） ======
  useEffect(() => {
    if (!landChangeNotification) return;
    setShowEventModal({ type: 'landChange', data: landChangeNotification });
    getState().landChangeNotification = null;
  }, [landChangeNotification]);

  // ====== 进入游戏时全屏（横屏为默认方向，竖屏也支持） ======
  // 不锁定方向，玩家可自由旋转切换横竖屏全屏

  // 移动端进入游戏后若未全屏，延迟展示全屏引导遮罩（兜底，横竖屏均触发）
  useEffect(() => {
    if (!isMobile) return;
    const t = setTimeout(() => {
      if (isFullscreen()) return;
      if (isFullscreenSupported()) {
        setShowFullscreenPrompt('fullscreen');
      } else if (isHarmonyOS()) {
        setShowFullscreenPrompt('harmonyos-homescreen');
      } else if (/iPad|iPhone|iPod/i.test(navigator.userAgent)) {
        setShowFullscreenPrompt('ios-homescreen');
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [isMobile]);

  // 一旦进入全屏就关闭引导遮罩
  useEffect(() => {
    if (isFullscreenActive) setShowFullscreenPrompt(null);
  }, [isFullscreenActive]);

  // ====== 边缘滑动防护：竖屏非全屏时阻止浏览器返回手势 ======
  useEffect(() => {
    if (!isMobile || !isPortrait || isFullscreenActive) return;
    let startX = 0;
    const onTouchStart = (e: TouchEvent) => {
      startX = e.touches[0]?.clientX ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      const x = e.touches[0]?.clientX ?? 0;
      // 从右往左滑且起点靠近右边缘 → 可能是浏览器返回手势
      const dx = x - startX;
      if (dx < -20 && startX > window.innerWidth - 60) {
        e.preventDefault();
      }
      // 从左往右滑且起点靠近左边缘 → 可能是浏览器前进手势  
      if (dx > 20 && startX < 60) {
        e.preventDefault();
      }
    };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [isMobile, isPortrait, isFullscreenActive]);

  // ====== 横竖屏切换按钮：同步当前屏幕方向 ======
  useEffect(() => {
    const update = () => setOrientationLock(screen.orientation?.type || null);
    update();
    screen.orientation?.addEventListener('change', update);
    return () => screen.orientation?.removeEventListener('change', update);
  }, []);

  // ====== 全局弹窗存在时抑制绿色成功提示（避免双重弹窗） ======
  useEffect(() => {
    const hasModal =
      showBuyDialog ||
      showReverseShop ||
      !!showEventModal ||
      showIdentityReveal ||
      showOwlPreview ||
      showBankruptcyLiquidation ||
      !!activeGlobalEvent ||
      !!comebackNotification ||
      !!landChangeNotification;
    setSuppressSuccessToast(hasModal);
    return () => { setSuppressSuccessToast(false); };
  }, [showBuyDialog, showReverseShop, showEventModal, showIdentityReveal,
      showOwlPreview, showBankruptcyLiquidation, activeGlobalEvent, comebackNotification, landChangeNotification]);

  const handleToggleOrientation = useCallback(async () => {
    const currentlyPortrait = isPortrait;
    const targetOrientation = currentlyPortrait ? 'landscape' : 'portrait';

    // 1. 确保进入全屏模式
    if (!isFullscreen()) {
      try {
        // 尝试从容器元素进入全屏（比 document 更可靠）
        const el = containerRef.current || document.documentElement;
        await el.requestFullscreen();
        await new Promise(r => setTimeout(r, 500));
      } catch {
        addToast('请先进入全屏模式', 'info');
        return;
      }
    }

    // 2. 尝试锁定屏幕方向
    try {
      await (screen.orientation as any).lock(targetOrientation);
      addToast(currentlyPortrait ? '已切换为横屏' : '已切换为竖屏', 'success');
      return;
    } catch {
      // lock 在部分浏览器不支持
    }

    // 3. 兜底：尝试退出全屏后按新方向重新全屏
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        await new Promise(r => setTimeout(r, 300));
      }
      // 用容器元素重新进入全屏
      const el = containerRef.current || document.documentElement;
      await el.requestFullscreen();
      // 再试一次 lock
      try { await (screen.orientation as any).lock(targetOrientation); return; } catch {}
    } catch {
      // 全屏重试也失败，继续
    }

    // 4. 最终兜底：震动+提示
    try { navigator.vibrate?.(50); } catch {}
    addToast(currentlyPortrait ? '请将手机横放' : '请将手机竖放', 'info');
  }, [isPortrait]);

  // ====== 全屏/非全屏切换 ======
  const handleToggleFullscreen = useCallback(async () => {
    if (isFullscreen()) {
      try {
        await document.exitFullscreen();
      } catch {
        // 某些浏览器不支持 exitFullscreen
      }
    } else {
      try {
        const el = containerRef.current || document.documentElement;
        await el.requestFullscreen();
      } catch {
        addToast('当前浏览器不支持全屏', 'info');
      }
    }
  }, []);

  const handleFullscreenPrompt = async () => {
    await requestFullscreen();
    setShowFullscreenPrompt(null);
  };

  // ====== 不再强制锁定横屏：允许玩家自由切换方向 ======

  // ====== 广播事件结束 ======
  const handleBroadcastDone = useCallback(() => {
    clearBroadcast();
  }, [clearBroadcast]);

  // ====== 初始化 PixiJS ======
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;
    if (boardRef.current) return;

    let ro: ResizeObserver | null = null;
    let initDone = false;
    let initAttempts = 0;

    const initPixi = () => {
      if (initDone) return;
      const rect = containerRef.current!.getBoundingClientRect();
      let w = rect.width;
      let h = rect.height;
      // 如果容器尺寸还不确定（0 或极小），延迟重试（最多10次）
      if ((w < 80 || h < 80) && initAttempts < 10) {
        initAttempts++;
        requestAnimationFrame(initPixi);
        return;
      }
      if (w < 80 || h < 80) {
        // 兜底：用 canvas CSS 尺寸（而非 window，避免与 CSS 显示尺寸不匹配导致变形）
        const cvs = canvasRef.current!;
        const cvsRect = cvs.getBoundingClientRect();
        w = cvsRect.width > 80 ? cvsRect.width : window.innerWidth;
        h = cvsRect.height > 80 ? cvsRect.height : window.innerHeight;
        console.warn(`[PixiJS] 容器尺寸异常 (${rect.width}x${rect.height})，兜底使用 canvas 尺寸 (${w}x${h})`);
      }
      initDone = true;
      initPixiWithSize(w, h);
    };

    const initPixiWithSize = (w: number, h: number) => {
      try {
        const renderer = new BoardRenderer(canvasRef.current!, w, h);
        renderer.setHoverCallback(
          (cellId, screenX, screenY) => {
            const cell = BOARD_CELLS[cellId];
            if (cell) setHoveredCell({ cellId, cell, x: screenX, y: screenY });
          },
          () => { setHoveredCell(null); },
        );
        renderer.setClickCallback((cellId) => {
          setSelectedCell(prev => prev === cellId ? null : cellId);
        });
        boardRef.current = renderer;
        // 立即标记 ready（棋盘先用矢量 fallback 渲染），tile 纹理加载完后重绘
        setReady(true);
        // 异步预加载 3D 卡通 tile 纹理，完成后触发一次棋盘重绘（升级为图片版）
        renderer.preloadTileTextures(() => {
          try {
            if (boardRef.current) {
              const currentBoard = useGameStore.getState().board;
              if (currentBoard && currentBoard.length > 0) {
                boardRef.current.renderBoard(currentBoard);
              }
            }
          } catch (e) {
            console.warn('[GameBoard] preloadTileTextures callback error:', e);
          }
        });
      } catch (err) {
        console.error('[PixiJS] init failed:', err);
      }
    };

    // 等两帧再初始化，确保 CSS 布局已稳定（修复联机加入时短暂变形）
    requestAnimationFrame(() => requestAnimationFrame(initPixi));

    ro = new ResizeObserver(() => {
      // 用 RAF 确保在下一帧布局稳定后再读取尺寸（修复联机加入时变形）
      requestAnimationFrame(() => {
        if (!boardRef.current || !containerRef.current) return;
        // 移动端竖屏非全屏：canvas 高度固定 45dvh，容器高度包含下方滚动区域
        // 用 canvas 自身尺寸，避免把容器总高度当画布高度导致变形
        const isMobilePortrait = window.innerWidth < 768 && window.innerHeight > window.innerWidth && !isFullscreen();
        const targetEl = isMobilePortrait ? canvasRef.current! : containerRef.current;
        const r = targetEl.getBoundingClientRect();

        // 排除极小尺寸（布局过渡期可能读到 0）
        if (r.width < 50 || r.height < 50) return;

        const w = r.width;
        const h = r.height;
        boardRef.current.resize(w, h);

        // 确保 canvas CSS 尺寸也匹配（防止 PIXI 内部与 DOM 尺寸不同步）
        const canvas = canvasRef.current!;
        if (canvas.style.width !== '100%' || canvas.style.height !== '100%') {
          canvas.style.width = '100%';
          canvas.style.height = '100%';
          canvas.style.display = 'block';
        }
      });
    });
    ro.observe(containerRef.current!);

    return () => {
      ro?.disconnect();
      if (boardRef.current) { boardRef.current.destroy(); boardRef.current = null; }
      setReady(false);
    };
  }, []);

  // ====== 渲染棋盘（board/players/properties 变更时全量重绘）======
  useEffect(() => {
    if (!boardRef.current || !ready) return;
    boardRef.current.renderBoard(board);
    // 🔑 动画进行中不要调用 renderPlayers，否则会销毁正在被 _stepNext 动画的 sprite
    if (!isAnimatingRef.current) {
      boardRef.current.renderPlayers(players);
    }
    boardRef.current.renderPlayerPositionHighlights(players);
    boardRef.current.renderBuildings(properties, board);
    boardRef.current.renderOwnership(properties, players);
  }, [board, players, properties, ready]);

  // ====== 预加载行走精灵表 ======
  useEffect(() => {
    if (!boardRef.current || !ready) return;
    players.forEach(p => {
      if (p.bankrupt || !p.characterId) return;
      const walkUrl = getCharWalkPath(p.characterId);
      boardRef.current?.loadWalkTextures(p.id, walkUrl);
    });
  }, [players, ready]);

  // ====== 联机模式：检测玩家位置变化，播放行棋动画 ======
  // 服务端权威：所有玩家的位置变更都来自 game:state
  useEffect(() => {
    if (!isOnline || !ready || !boardRef.current) return;

    players.forEach(p => {
      const lastPos = lastKnownPositionsRef.current[p.id];
      if (lastPos !== undefined && lastPos !== p.position) {
        // 跳过破产玩家
        if (p.bankrupt) {
          console.log('[ANIM] skip bankrupt player', p.name, 'pos:', lastPos, '→', p.position);
          lastKnownPositionsRef.current[p.id] = p.position;
          return;
        }

        const from = lastPos;
        const to = p.position;
        const isMe = p.name.trim().toLowerCase() === (myPlayerName || '').trim().toLowerCase();
        // 🔑 用 getState() 动态读取，避免闭包中 turnPhase 过期
        const currentTurnPhase = getState().turnPhase;
        console.log(`[ANIM] position changed: ${p.name} ${from}→${to} isMe=${isMe} initialSyncDone=${initialSyncDoneRef.current} isAnim=${isAnimatingRef.current} turnPhase=${currentTurnPhase}`);

        // 🔑 卡牌瞬移检测：turnPhase='rolling' 时的位置变化来自反转卡（如时光倒流），
        // 跳过动画直接传送。正常掷骰子移动时 turnPhase 已变为 'moving'/'buyDecision' 等。
        // 使用 getState() 而非闭包变量，确保读取最新状态
        if (currentTurnPhase === 'rolling') {
          console.log('[ANIM] card teleport detected (turnPhase=rolling via getState), instant teleport without animation');
          lastKnownPositionsRef.current[p.id] = p.position;
          // 直接更新棋子位置 (瞬移)
          boardRef.current?.teleportTo(p.id, to);
          return;
        }

        const path = calcPath(from, to);

        if (path.length > 0 && initialSyncDoneRef.current) {
          setIsMoving(true);
          isAnimatingRef.current = true;
          console.log('[ANIM] starting animation, path:', path, 'isAnimatingRef=true');
          // 计算步数用于显示
          const state = getState();
          const steps = state.diceResult ? (state.diceResult[0] + state.diceResult[1]) : path.length;
          setMoveSteps(steps);

          boardRef.current?.animateStepByStep(
            p.id, path,
            (cellId) => {
              const cell = BOARD_CELLS[cellId];
              setCurrentStepCell(cell ? `${cell.emoji || ''} ${cell.name}` : '');
            },
            () => {
              console.log('[ANIM] animation onComplete, deferredDialog=', deferredDialogRef.current);
              setCurrentStepCell('');
              setIsMoving(false);
              setMoveSteps(0);
              isAnimatingRef.current = false;
              // 🔑 动画结束后检查是否有延迟弹窗需要弹出
              const deferred = deferredDialogRef.current;
              if (deferred) {
                console.log('[ANIM] firing deferred dialog:', deferred);
                deferredDialogRef.current = null;
                // 使用 setTimeout 确保 React 状态已更新 (isMoving→false 已提交)
                setTimeout(() => {
                  console.log('[ANIM] showActionDialogRef firing for:', deferred);
                  showActionDialogRef.current(deferred);
                }, 0);
              } else {
                console.log('[ANIM] no deferred dialog to fire');
              }
            }
          );
        } else {
          console.log(`[ANIM] skipping animation: pathLen=${path.length} initialSyncDone=${initialSyncDoneRef.current}`);
        }
      }
      lastKnownPositionsRef.current[p.id] = p.position;
    });

    // 首次同步完成后标记（后续的位置变化才播放动画）
    if (!initialSyncDoneRef.current) {
      console.log('[ANIM] initialSyncDoneRef now true');
      initialSyncDoneRef.current = true;
    }
  }, [players, isOnline, ready, myPlayerName]);

  // ====== 联机模式：服务端广播 turnPhase → 人类玩家显示对应弹窗 ======
  // ⚠️ 必须定义在动画 effect 之后，确保 React 执行顺序：
  //    1. 动画 effect 先设 isAnimatingRef = true
  //    2. 本 effect 再检查 isAnimatingRef → 正确延迟弹窗
  const [lastActionPhase, setLastActionPhase] = useState<string | null>(null);
  const lastActionPhaseRef = useRef<string | null>(null);
  const deferredDialogRef = useRef<string | null>(null);

  // 回合回到 rolling 时重置标记（准备下一轮弹窗）
  useEffect(() => {
    if (turnPhase === 'rolling') {
      lastActionPhaseRef.current = null;
      setLastActionPhase(null);
    }
  }, [turnPhase]);

  // 检测 turnPhase 变化 → 延迟弹窗（等动画结束）
  useEffect(() => {
    if (!isOnline || !isMyTurn) {
      if (isOnline) console.log('[DIALOG] skip: isOnline=', isOnline, 'isMyTurn=', isMyTurn);
      return;
    }
    if (!currentPlayer || currentPlayer.isBot || currentPlayer.bankrupt) {
      console.log('[DIALOG] skip: currentPlayer bot/bankrupt');
      return;
    }

    const actionPhases = ['buyDecision', 'upgradeOffer', 'cellEvent', 'fateEvent',
      'taxEvent', 'jailEvent', 'payRent', 'reverseShop', 'auctionEvent', 'casinoEvent'];
    if (turnPhase === 'action') {
      console.log('[DIALOG] skip: internal action phase (waiting for applyCellTurnPhase)');
      return;
    }
    if (!actionPhases.includes(turnPhase)) {
      console.log('[DIALOG] skip: turnPhase', turnPhase, 'not in actionPhases');
      return;
    }

    if (lastActionPhaseRef.current === turnPhase) {
      console.log('[DIALOG] skip: duplicate phase', turnPhase);
      return;
    }
    lastActionPhaseRef.current = turnPhase;
    setLastActionPhase(turnPhase);
    console.log('[DIALOG] detected action phase:', turnPhase, 'isAnimatingRef=', isAnimatingRef.current);

    // 动画进行中 → 延迟到 onComplete 回调触发
    if (isAnimatingRef.current) {
      deferredDialogRef.current = turnPhase;
      console.log('[DIALOG] deffered dialog:', turnPhase);
      return;
    }

    // 无动画（如特殊格子处理）→ 直接弹窗
    console.log('[DIALOG] showing dialog immediately for:', turnPhase);
    showActionDialog(turnPhase);
  }, [turnPhase, isOnline, isMyTurn, currentPlayer]);

  // 统一的弹窗展示逻辑
  const showActionDialog = useCallback((phase: string) => {
    console.log('[SHOWDLG] showActionDialog called for phase:', phase, 'currentPlayer:', currentPlayer?.name, 'pos:', currentPlayer?.position);
    if (!currentPlayer) { console.log('[SHOWDLG] no currentPlayer, returning'); return; }
    const cell = board[currentPlayer.position];
    if (!cell) { console.log('[SHOWDLG] no cell at position', currentPlayer.position); return; }
    console.log('[SHOWDLG] cell:', cell.type, cell.name);

    const prop = properties.find(p => p.cellId === currentPlayer.position);

    switch (phase) {
      case 'buyDecision':
        if (cell.type === 'property' && !prop) {
          if (currentPlayer.characterId === 'owl' && !owlCellInfo) {
            setOwlCellInfo({ cellId: currentPlayer.position, cellName: cell.name, cellPrice: cell.price || 0 });
            setShowOwlPreview(true);
          } else {
            setShowBuyDialog(true);
          }
        }
        break;
      case 'upgradeOffer':
        if (prop && cell.type === 'property' && cell.upgradeCost && (prop.buildingLevel || 0) < 3) {
          setUpgradeOffer({ cellId: currentPlayer.position, cell, prop });
        }
        break;
      case 'fateEvent':
        {
          const fateId = currentPlayer?.fateCardHistory?.slice(-1)[0];
          const fateCard = fateId ? FATE_CARDS.find(c => c.id === fateId) : null;
          setShowEventModal({
            type: 'fate',
            data: fateCard || { id: 'unknown', name: '命运卡', emoji: '🃏', description: '你抽到了一张命运卡！', effect: '' }
          });
        }
        break;
      case 'cellEvent':
        setShowEventModal({
          type: 'cellEvent',
          data: { emoji: cell.emoji || '⚡', title: cell.name, description: cell.description || '发生了特殊事件！', color: cell.type === 'start' ? '#4CAF50' : '#E040FB' }
        });
        break;
      case 'taxEvent':
        setShowEventModal({
          type: 'cellEvent',
          data: { emoji: cell.emoji || '💰', title: cell.name, description: cell.description || '缴纳高额税费！', color: '#FF9800' }
        });
        break;
      case 'jailEvent':
        setShowEventModal({
          type: 'cellEvent',
          data: { emoji: cell.emoji || '🚔', title: cell.name, description: cell.description || '你被送进了监狱！', color: '#F44336' }
        });
        break;
      case 'payRent':
        if (prop && prop.ownerId !== currentPlayer.id) {
          const owner = players.find(p => p.id === prop.ownerId);
          setShowEventModal({
            type: 'cellEvent',
            data: { emoji: '💸', title: `向 ${owner?.name || '??'} 支付租金`, description: `${cell.name} 属于 ${owner?.name || '他人'}`, color: '#FF9800' }
          });
        }
        break;
      case 'reverseShop':
        setShowReverseShop(true);
        break;
      case 'auctionEvent':
        setShowEventModal({
          type: 'cellEvent',
          data: { emoji: cell.emoji || '🔨', title: cell.name, description: cell.description || '拍卖获得道具！', color: '#FFC107' }
        });
        break;
      case 'casinoEvent':
        setShowEventModal({
          type: 'cellEvent',
          data: { emoji: cell.emoji || '🎰', title: cell.name, description: cell.description || '试试你的运气！', color: '#E040FB' }
        });
        break;
    }
  }, [board, currentPlayer, properties, players, owlCellInfo]);
  const showActionDialogRef = useRef(showActionDialog);
  showActionDialogRef.current = showActionDialog;

  // 初始化 lastKnownPositions（首次挂载时记录所有玩家位置）
  useEffect(() => {
    const positions: Record<string, number> = {};
    players.forEach(p => { positions[p.id] = p.position; });
    lastKnownPositionsRef.current = positions;
    initialSyncDoneRef.current = false;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ====== 掷骰子倒计时：我的回合 30 秒超时自动掷骰 ======
  // doRoll 的 ref（需在 useEffect 之前声明，避免闭包过期）
  const doRollRef = useRef<() => void>(() => {});
  
  useEffect(() => {
    clearCountdownTimers();
    setDiceCountdown(0);

    // 只在联机模式下、轮到我、phase 为 rolling、非 Bot 时启动倒计时
    if (!isOnline || !isMyTurn || turnPhase !== 'rolling' || currentPlayer?.isBot || currentPlayer?.bankrupt) {
      return;
    }

    console.log('[Countdown] 启动30秒掷骰倒计时');
    setDiceCountdown(30);

    // 每秒递减
    countdownIntervalRef.current = window.setInterval(() => {
      setDiceCountdown(prev => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = 0; }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // 30秒后自动掷骰
    autoRollTimeoutRef.current = window.setTimeout(() => {
      console.warn('[Countdown] ⏰ 30秒超时，自动掷骰子');
      doRollRef.current();
    }, 30000);

    return () => clearCountdownTimers();
  }, [isOnline, isMyTurn, turnPhase, currentPlayer?.isBot, currentPlayer?.bankrupt, clearCountdownTimers]);

  // doRoll 的 ref（避免闭包过期）

  // ====== 选中格子单独更新（不触发全量重绘，避免闪烁）======
  useEffect(() => {
    if (!boardRef.current || !ready) return;
    boardRef.current.setSelectedCell(selectedCell);
  }, [selectedCell, ready]);

  // ====== 通用移动动画触发器：rolling→moving 或 diceChoice→moving ======
  // 联机模式：服务端处理，客户端不执行本地逻辑
  useEffect(() => {
    if (isOnline) return;
    const prevPhase = prevTurnPhaseRef.current;
    prevTurnPhaseRef.current = turnPhase;
    if (turnPhase !== 'moving') return;

    // 需要触发动画的两种场景：从 rolling 进入（非兔子）或从 diceChoice 进入（兔子已选骰子）
    const shouldAnimate = prevPhase === 'rolling' || prevPhase === 'diceChoice';
    if (!shouldAnimate) return;

    const s = getState();
    const cp = s.players[s.currentPlayerIndex];
    const isRabbit = cp?.characterId === 'rabbit';
    console.log('[MoveAnim] phase triggered, cp:', cp?.name, 'isRabbit:', isRabbit, 'prevPos:', cp?.previousPosition, 'pos:', cp?.position, 'diceResult:', s.diceResult);

    if (!cp || !s.diceResult) return;

    // 兔子：只走单个骰子值；非兔子：走两个骰子和
    const steps = isRabbit ? s.diceResult[0] : s.diceResult[0] + s.diceResult[1];
    const from = cp.previousPosition ?? 0;
    const to = cp.position;
    const path = calcPath(from, to);

    console.log('[MoveAnim] steps:', steps, 'from:', from, 'to:', to, 'path:', path);

    setMoveSteps(steps);
    setIsMoving(true);

    const playerId = cp.id;
    const timeout = setTimeout(() => {
      // 动画完成后再次读最新位置，保证 doCellAction 用的是正确坐标
      const finalPos = getState().players[getState().currentPlayerIndex]?.position ?? to;
      if (!boardRef.current) {
        setCurrentStepCell('');
        doCellAction(finalPos, playerId);
        return;
      }
      boardRef.current.animateStepByStep(
        playerId, path,
        (cellId) => {
          const cell = BOARD_CELLS[cellId];
          setCurrentStepCell(cell ? `${cell.emoji || ''} ${cell.name}` : '');
        },
        () => {
          setCurrentStepCell('');
          const latestPos = getState().players[getState().currentPlayerIndex]?.position ?? to;
          doCellAction(latestPos, playerId);
        }
      );
    }, 500);

    return () => clearTimeout(timeout);
  }, [turnPhase]);

  // ====== 计算移动路径 ======
  const calcPath = (from: number, to: number): number[] => {
    const path: number[] = [];
    let curr = from;
    while (curr !== to) {
      curr = (curr + 1) % 40;
      path.push(curr);
    }
    return path;
  };

  // ====== 结束当前玩家操作 ======
  const finishTurn = useCallback((playerId: string) => {
    // 联机模式：服务端驱动回合推进，客户端不执行本地 nextTurn
    if (isOnlineRef.current) return;
    try {
      const s = getState();
      const p = s.players.find(x => x.id === playerId);
      console.log('[finishTurn] playerId:', playerId, 'currentIndex:', s.currentPlayerIndex, 'phase:', s.turnPhase);
      if (!p || p.bankrupt) { console.log('[finishTurn] 玩家不存在或破产，nextTurn'); s.nextTurn(); finishTurnSync(); return; }
      if (s.checkBankruptcy(p.id)) {
        console.log('[finishTurn] 玩家破产检查，进入清算流程');
        s.startBankruptcyLiquidation(p.id);
        setAutoPlayBot(false);
        setMoveSteps(0);
        setIsMoving(false);
        return;
      }
      s.checkComeback(p.id);
      if ((p.immuneTurns ?? 0) > 0) {
        s.addLog(p.id, `守财奴免疫剩余${p.immuneTurns - 1}回合`, 'system');
      }
      console.log('[finishTurn] 调用 nextTurn');
      s.nextTurn();
      console.log('[finishTurn] nextTurn 完成，新index:', getState().currentPlayerIndex, '新phase:', getState().turnPhase);
      // 联机模式：同步最新状态给所有玩家
      finishTurnSync();
    } catch (err) {
      console.error('[finishTurn] err:', err);
      try { getState().nextTurn(); finishTurnSync(); } catch (_) {}
    }
    setAutoPlayBot(false);
    setMoveSteps(0);
    setIsMoving(false);
  }, [getState]);

  // 联机模式下回合结束同步状态（服务端权威：无需客户端同步）
  const finishTurnSync = useCallback(() => {
    // 服务端权威模式：回合推进由服务端 advanceTurn + broadcastGameState 驱动
    // 客户端无需主动同步
  }, []);

  // ====== 处理格子效果（带广播）======
  const doCellAction = useCallback((cellId: number, playerId: string) => {
    try {
      const s = getState();
      const cell = s.board[cellId];
      const cp = s.players.find(p => p.id === playerId);
      if (!cell || !cp) { s.nextTurn(); return; }

      switch (cell.type) {
        case 'property': {
          const prop = s.properties.find(p => p.cellId === cellId);
          if (prop && prop.ownerId !== cp.id) {
            const owner = s.players.find(x => x.id === prop.ownerId);
            const rent = s.payRent(cp.id, prop.ownerId, cellId);
            if (rent > 0) {
              setBroadcast(`${cp.name} 向 ${owner?.name} 支付租金 ¥${rent}`, 'warning');
            }
            finishTurn(cp.id);
          } else if (!prop) {
            if (cp.isBot) { handleBotBuy(cp, cell, cellId); finishTurn(cp.id); }
            else if (cp.characterId === 'owl') {
              // 猫头鹰学者：先预览命运卡再决定
              setOwlCellInfo({ cellId, cellName: cell.name, cellPrice: cell.price || 0 });
              setShowOwlPreview(true);
              s.setTurnPhase('buyDecision');
            }
            else { setShowBuyDialog(true); s.setTurnPhase('buyDecision'); }
          } else {
            // 自己的地产
            if (cp.isBot) {
              // Bot：自动升级判定
              if (prop && prop.buildingLevel < 3 && cell.upgradeCost) {
                handleBotUpgrade(cp, cell, cellId, prop);
              }
              setBroadcast(`${cp.name} 拥有 ${cell.name}`, 'info');
              finishTurn(cp.id);
            } else {
              // 人类玩家：若未满级且有升级成本 → 弹出升级选择弹窗
              if (prop && prop.buildingLevel < 3 && cell.upgradeCost) {
                setUpgradeOffer({ cellId, cell, prop });
                s.setTurnPhase('buyDecision');
              } else {
                if (prop && prop.buildingLevel >= 3) {
                  setBroadcast(`${cp.name} 的 ${cell.name} 已满级（酒店）`, 'info');
                } else {
                  setBroadcast(`${cp.name} 拥有 ${cell.name}`, 'info');
                }
                finishTurn(cp.id);
              }
            }
          }
          break;
        }
        case 'chance': {
          console.log('[doCellAction] 命运格：玩家', cp.name);
          const card = s.drawFateCard(cp.id);
          console.log('[doCellAction] 命运卡抽出：', card?.name, '当前phase：', s.turnPhase);
          if (cp.isBot) { console.log('[doCellAction] Bot命运卡，直接finishTurn'); finishTurn(cp.id); }
          else { console.log('[doCellAction] 人类命运卡，显示Modal'); setShowEventModal({ type: 'fate', data: card }); s.setTurnPhase('chanceDraw'); }
          break;
        }
        case 'tax':
          s.payTax(cp.id);
          if (cp.isBot) { finishTurn(cp.id); }
          else { setShowEventModal({ type: 'cellEvent', data: { emoji: cell.emoji || '💰', title: cell.name, description: cell.description || '缴纳高额税费！', color: '#FF9800' } }); s.setTurnPhase('cellEvent'); }
          break;
        case 'event': {
          // 踩到事件格 → 强制触发全局事件（静默模式：EventModal 已显示，不弹 Broadcast）
          const result = s.checkAndTriggerGlobalEvent(true, true);
          if (result) {
            if (cp.isBot) { finishTurn(cp.id); }
            else {
              setShowEventModal({
                type: 'cellEvent',
                data: {
                  emoji: result.event.emoji,
                  title: `全局事件：${result.event.name}`,
                  description: result.event.description,
                  color: result.event.id === 'economic_winter' ? '#64B5F6' : result.event.id === 'golden_age' ? '#FFD54F' : '#E040FB'
                }
              });
              s.setTurnPhase('cellEvent');
            }
          } else {
            if (cp.isBot) { finishTurn(cp.id); }
            else { setShowEventModal({ type: 'cellEvent', data: { emoji: cell.emoji || '⚡', title: cell.name, description: cell.description || '发生了特殊事件！', color: '#E040FB' } }); s.setTurnPhase('cellEvent'); }
          }
          break;
        }
        case 'start':
          if (cp.isBot) { finishTurn(cp.id); }
          else { setShowEventModal({ type: 'cellEvent', data: { emoji: cell.emoji || '🚩', title: cell.name, description: cell.description || '经过起点，获得 ¥200 奖励！', color: '#4CAF50' } }); s.setTurnPhase('cellEvent'); }
          break;
        case 'jail':
          s.sendToJail(cp.id);
          if (cp.isBot) { finishTurn(cp.id); }
          else { setShowEventModal({ type: 'cellEvent', data: { emoji: cell.emoji || '🚔', title: cell.name, description: cell.description || '你被送进了监狱！需要掷出双数或支付 ¥200 保释。', color: '#F44336' } }); s.setTurnPhase('cellEvent'); }
          break;
        case 'reverseShop':
          if (cp.isBot) {
            const affordable = REVERSE_CARDS.filter(c => (c.cost ?? 999) <= cp.money);
            if (affordable.length > 0) {
              const card = affordable[Math.floor(Math.random() * affordable.length)];
              s.deductMoney(cp.id, card.cost!);
              s.addReverseCard(cp.id, card.id as any);
              setBroadcast(`${cp.name} 在反转商店购买了 ${card.emoji}${card.name}，花费 ¥${card.cost!}`, 'success');
            }
            finishTurn(cp.id);
          } else {
            setShowReverseShop(true);
            s.setTurnPhase('reverseShop');
          }
          break;
        case 'auction': {
          const cardIds = REVERSE_CARDS.map(c => c.id as any);
          const won = cardIds[Math.floor(Math.random() * cardIds.length)];
          s.addReverseCard(cp.id, won);
          if (cp.isBot) { finishTurn(cp.id); }
          else { setShowEventModal({ type: 'cellEvent', data: { emoji: cell.emoji || '🔨', title: cell.name, description: `拍卖获得反转卡：${REVERSE_CARDS.find(c => c.id === won)?.emoji || ''} ${REVERSE_CARDS.find(c => c.id === won)?.name || ''}`, color: '#FFC107' } }); s.setTurnPhase('cellEvent'); }
          break;
        }
        case 'casino': {
          const win = Math.random() > 0.5;
          if (win) { s.addMoney(cp.id, 100); }
          else { s.deductMoney(cp.id, 100); }
          if (cp.isBot) { finishTurn(cp.id); }
          else { setShowEventModal({ type: 'cellEvent', data: { emoji: cell.emoji || '🎰', title: cell.name, description: win ? '运气不错！赢了 ¥100！' : '运气不佳，输了 ¥100。', color: win ? '#4CAF50' : '#F44336' } }); s.setTurnPhase('cellEvent'); }
          break;
        }
        default: finishTurn(cp.id);
      }
    } catch (err) {
      console.error('[doCellAction] err:', err);
      try { getState().nextTurn(); } catch (_) {}
    }
  }, [getState, finishTurn, setBroadcast]);

  // ====== Bot AI ======
  const handleBotBuy = useCallback((bot: any, cell: BoardCell, cellId: number) => {
    const s = getState();
    const sameStreet = bot.properties.filter((pid: number) => s.board[pid]?.street === cell.street).length;
    const rent = cell.rentTable?.[0] || 0;
    const payback = rent > 0 ? Math.ceil((cell.price || 0) / rent) : 999;
    // 更激进的购买策略：
    // 1. 去掉 +100 保底，只要有地价就敢买
    // 2. sameStreet >= 1 就值得买（原来要 >= 2）
    // 3. 回本周期放宽到 10 回合内（原来 6）
    // 4. 随机购买概率提升到 0.6（原来 0.4）
    // 5. 地产少于 3 块时，主动扩张概率 0.8
    const hasEnoughMoney = bot.money >= (cell.price || 999);
    const isGoodDeal = payback <= 10;
    const hasStreetPresence = sameStreet >= 1;
    const fewProperties = bot.properties.length < 3 && Math.random() < 0.8;
    const randomFactor = Math.random() < 0.6;
    const shouldBuy = hasEnoughMoney && (hasStreetPresence || isGoodDeal || fewProperties || randomFactor);
    if (shouldBuy) {
      s.buyProperty(bot.id, cellId);
      setBroadcast(`${bot.name} 买了 ${cell.name}，花费 ¥${cell.price}`, 'success');
    }
  }, [getState, setBroadcast]);

  // ====== Bot 升级 AI ======
  const handleBotUpgrade = useCallback((bot: any, cell: BoardCell, cellId: number, prop: any) => {
    const s = getState();
    const upgradeCost = cell.upgradeCost || 0;
    if (!upgradeCost || prop.buildingLevel >= 3) return;
    // Bot 在有足够余钱时升级（留 300 保底）
    const shouldUpgrade = bot.money >= upgradeCost + 300 && (Math.random() < 0.5 || prop.buildingLevel === 0);
    if (shouldUpgrade) {
      const ok = s.upgradeProperty(bot.id, cellId);
      if (ok) {
        const levelNames = ['房子', '别墅', '酒店'];
        setBroadcast(
          `${bot.name} 升级了 ${cell.name} → ${levelNames[prop.buildingLevel]} ⭐${prop.buildingLevel + 1}，花费 ¥${upgradeCost}`,
          'success'
        );
      }
    }
  }, [getState, setBroadcast]);

  // ====== 掷骰子（服务端权威模式：发送命令，服务端执行并广播状态）======
  const doRoll = useCallback(() => {
    const s = getState();
    console.log('[ROLL] doRoll called: turnPhase=', s.turnPhase, 'cpIdx=', s.currentPlayerIndex,
      'players=', s.players?.map(p => `${p.name}(bot=${p.isBot})`).join(','),
      'myName=', myPlayerName, 'isOnline=', isOnline);
    if (s.turnPhase !== 'rolling') { console.log('[ROLL] BLOCKED: turnPhase is', s.turnPhase); return; }
    const cp = s.players[s.currentPlayerIndex];
    if (!cp || cp.isBot || cp.bankrupt) { console.log('[ROLL] BLOCKED: cp invalid', cp?.name, 'isBot=', cp?.isBot, 'bankrupt=', cp?.bankrupt); return; }
    // 联机模式：非本人回合不能操作
    if (isOnline && cp.name.trim().toLowerCase() !== (myPlayerName || '').trim().toLowerCase()) {
      console.log('[ROLL] BLOCKED: name mismatch cp=', cp.name, 'my=', myPlayerName);
      return;
    }

    if (cp.inJail) {
      // 在监狱中：弹出保释选择
      if (cp.jailTurns <= 0) {
        // 刑期已满
        if (isOnline) {
          useRoomStore.getState().sendBailCommand();
        } else {
          s.payBail(cp.id);
          s.nextTurn();
        }
        return;
      } else {
        setJailChoice(true);
        return;
      }
    }

    if (isOnline) {
      // 服务端权威：发送掷骰子命令
      useRoomStore.getState().sendRollCommand();
      return;
    }

    // 热座模式：本地掷骰子 + 移动
    const result = s.rollDiceAction();
    // 兔子赌徒：rollDiceAction 已设置 turnPhase='diceChoice'，等待玩家选骰子
    if (cp.characterId === 'rabbit') return;

    // 非兔子：rollDiceAction 只设置了 diceResult，需要手动推进流程
    const totalSteps = result[0] + result[1];
    s.setTurnPhase('moving');
    s.movePlayer(cp.id, totalSteps);
  }, [getState, isOnline, myPlayerName]);
  doRollRef.current = doRoll;
  const handleJailPayBail = useCallback(() => {
    setJailChoice(false);
    if (isOnline) {
      console.log('[jail] sending game:bail to server');
      useRoomStore.getState().sendBailCommand();
      return;
    }
    // 热座模式：本地执行
    const s = getState();
    const cp = s.players[s.currentPlayerIndex];
    if (!cp) return;
    s.payBail(cp.id);
    s.nextTurn();
  }, [isOnline]);

  // ====== 监狱：尝试掷双数 ======
  const handleJailTryDoubles = useCallback(() => {
    setJailChoice(false);
    if (isOnline) {
      console.log('[jail] sending game:jail_doubles to server');
      useRoomStore.getState().sendJailDoublesCommand();
      return;
    }
    // 热座模式：本地执行掷骰子（游戏引擎处理监狱掷双数逻辑）
    const s = getState();
    const cp = s.players[s.currentPlayerIndex];
    if (!cp) return;
    s.rollDiceAction();
  }, [isOnline]);

  // ====== Bot 控制器（统一管理）======
  const doCellActionRef = useRef(doCellAction);
  doCellActionRef.current = doCellAction;
  const getStateRef = useRef(getState);
  getStateRef.current = getState;
  const finishTurnRef = useRef(finishTurn);
  finishTurnRef.current = finishTurn;

  const autoPlayingRef = useRef(false);       // 防重入
  const botDoneRef = useRef(false);           
  const botNormalTimerRef = useRef<number>(0);
  const botStuckTimerRef = useRef<number>(0);
  const countdownTimerRef = useRef<number>(0);
  const [botCountdown, setBotCountdown] = useState(15);

  // ====== Bot 回合：服务端权威模式，Bot 由服务端调度执行 ======
  // 客户端不再执行 Bot AI，仅响应服务端广播的 game:state
  // isBot 标记由服务端权威维护，掉线=Bot、重连=人类
  // 客户端角色：展示 Bot 状态 + 等待指示器

  // ====== 热座模式：Bot 自动执行 ======
  // 服务端权威重构将 Bot AI 移至服务端，但热座模式仍需客户端本地执行
  useEffect(() => {
    if (isOnline) return;
    const cp = players[currentPlayerIndex];
    if (!cp || !cp.isBot || cp.bankrupt) return;
    if (turnPhase !== 'rolling') return;

    console.log('[HotSeatBot] triggering bot turn:', cp.name);
    const delay = 800 + Math.random() * 700;
    setBotCountdown(Math.ceil(delay / 1000));

    const countdownInterval = setInterval(() => {
      setBotCountdown(c => {
        if (c <= 1) { clearInterval(countdownInterval); return 0; }
        return c - 1;
      });
    }, 1000);

    const botTimer = setTimeout(() => {
      const s = getState();
      const bot = s.players[s.currentPlayerIndex];
      if (!bot || !bot.isBot || bot.bankrupt || s.turnPhase !== 'rolling') {
        clearInterval(countdownInterval);
        return;
      }
      setBotCountdown(0);
      clearInterval(countdownInterval);

      // === 监狱处理 ===
      if (bot.inJail) {
        if (bot.jailTurns > 0) {
          const roll = s.rollDiceAction();
          if (roll && roll[0] === roll[1]) {
            // 掷出双数，出狱并移动
            s.setTurnPhase('moving');
            s.movePlayer(bot.id, roll[0] + roll[1]);
          } else {
            // 未掷出双数，结束回合
            finishTurnRef.current(bot.id);
          }
        } else {
          s.payBail(bot.id);
          s.nextTurn();
        }
        return;
      }

      // === 正常掷骰子 ===
      const result = s.rollDiceAction();
      if (bot.characterId === 'rabbit') {
        // 兔子赌徒：自动选高值
        setTimeout(() => {
          const st = getState();
          if (st._diceOptionHigh !== null) {
            st.chooseDiceSteps(st._diceOptionHigh);
          }
        }, 400);
        return;
      }

      // 非兔子：手动推进
      const totalSteps = result[0] + result[1];
      s.setTurnPhase('moving');
      s.movePlayer(bot.id, totalSteps);
    }, delay);

    return () => {
      clearTimeout(botTimer);
      clearInterval(countdownInterval);
    };
  }, [isOnline, players, currentPlayerIndex, turnPhase]);

  // ====== 安全兜底：回合切换时重置移动状态 ======

  // ====== 安全兜底：回合切换时重置移动状态 ======
  // 防止破产清算等异常路径中 isMoving 未被正确清除
  useEffect(() => {
    if (turnPhase === 'rolling') {
      setIsMoving(false);
      setMoveSteps(0);
    }
  }, [turnPhase]);

  // ====== 页面卸载清理 ======
  useEffect(() => {
    return () => {
      clearCountdownTimers();
    };
  }, [clearCountdownTimers]);

  // ====== 购买确认 ======
  const handleBuyConfirm = useCallback((buy: boolean) => {
    setShowBuyDialog(false);
    if (isOnline) {
      console.log('[buy] sending', buy ? 'buy' : 'skip_buy', 'to server');
      useRoomStore.getState().sendBuyCommand(buy);
      return;
    }
    // 热座模式：本地执行
    const s = getState();
    const cp = s.players[s.currentPlayerIndex];
    if (!cp) return;
    if (buy) {
      s.buyProperty(cp.id, cp.position);
      setBroadcast(`${cp.name} 购买了地皮`, 'success');
    }
    finishTurn(cp.id);
  }, [isOnline, finishTurn, setBroadcast]);

  // ====== 反转商店关闭 ======
  const handleReverseShopClose = useCallback(() => {
    setShowReverseShop(false);
    if (isOnline) {
      useRoomStore.getState().sendCellEventCloseCommand();
      return;
    }
    // 热座模式：直接推进回合
    const s = getState();
    finishTurn(s.players[s.currentPlayerIndex]?.id || '');
  }, [isOnline, finishTurn]);

  // ====== 地产升级确认 ======
  const handleUpgradeConfirm = useCallback(() => {
    setUpgradeOffer(null);
    if (isOnline) {
      useRoomStore.getState().sendUpgradeCommand(true);
      return;
    }
    const s = getState();
    const cp = s.players[s.currentPlayerIndex];
    if (!cp) return;
    s.upgradeProperty(cp.id, cp.position);
    setBroadcast(`${cp.name} 升级了地产`, 'success');
    finishTurn(cp.id);
  }, [isOnline, finishTurn, setBroadcast]);

  const handleUpgradeSkip = useCallback(() => {
    setUpgradeOffer(null);
    if (isOnline) {
      useRoomStore.getState().sendUpgradeCommand(false);
      return;
    }
    const s = getState();
    finishTurn(s.players[s.currentPlayerIndex]?.id || '');
  }, [isOnline, finishTurn]);

  // ====== 命运卡/事件/绝境/地产变动关闭 ======
  const handleEventClose = useCallback(() => {
    console.log('[handleEventClose] 事件关闭');
    const currentType = showEventModal?.type;
    setShowEventModal(null);

    // 地产变动弹窗：确认后闪烁对应格子
    if (currentType === 'landChange' && showEventModal?.data?.cellId !== undefined) {
      boardRef.current?.flashCell(showEventModal.data.cellId);
      return;
    }

    // 绝境反击弹窗不推进回合（纯通知）
    if (currentType === 'comeback') return;

    if (isOnline) {
      // 联机模式：通知服务器事件已关闭
      useRoomStore.getState().sendCellEventCloseCommand();
      return;
    }

    // 热座模式：本地推进回合
    const s = getState();
    const cp = s.players[s.currentPlayerIndex];
    if (cp) finishTurn(cp.id);
  }, [isOnline, finishTurn, showEventModal?.type, showEventModal?.data]);

  // ====== 猫头鹰学者：预览后决定 ======
  const handleOwlDecide = useCallback((buy: boolean) => {
    setShowOwlPreview(false);
    if (isOnline) {
      // 联机模式：发送服务器命令
      useRoomStore.getState().sendBuyCommand(buy);
      setOwlCellInfo(null);
      return;
    }
    const s = getState();
    const cp = s.players[s.currentPlayerIndex];
    if (!cp || !owlCellInfo) { s.nextTurn(); return; }

    if (buy) {
      s.buyProperty(cp.id, owlCellInfo.cellId);
      setBroadcast(`${cp.name} 购买了 ${owlCellInfo.cellName}`, 'success');
    }
    finishTurn(cp.id);
    setOwlCellInfo(null);
  }, [isOnline, getState, finishTurn, setBroadcast, owlCellInfo]);

  // ====== 派生数据 ======
  const currentChar = useMemo(() => currentPlayer ? CHARACTERS.find(c => c.id === currentPlayer.characterId) : undefined, [currentPlayer]);
  // 联机模式：只显示本地玩家自己的身份 | 热座模式：显示当前回合玩家的身份
  const identityPlayer = isOnline ? localPlayer : currentPlayer;
  const currentIdentity = useMemo(() =>
    identityPlayer?.identityId ? IDENTITIES.find(i => i.id === identityPlayer.identityId) : null, [identityPlayer]
  );

  // ====== 暴露身份（服务端权威）======
  const handleRevealIdentity = useCallback(() => {
    // 联机模式用 identityPlayer（=localPlayer），热座用 currentPlayer
    const revealer = isOnline ? identityPlayer : currentPlayer;
    if (!revealer || revealer.identityRevealed) return;
    if (isOnline) {
      console.log('[identity] sending game:reveal_identity to server');
      useRoomStore.getState().sendRevealIdentityCommand();
      setShowIdentityReveal(false);
      return;
    }
    // 热座模式：本地暴露身份
    getState().revealIdentity(revealer.id);
    setShowIdentityReveal(false);
  }, [currentPlayer, identityPlayer, isOnline, getState]);

  // ====== 使用反转卡 ======
  const handleUseCard = useCallback((cardId: string, targetId?: string, extra?: any) => {
    if (isOnline) {
      // 联机模式：CardHand 传递的是 player.id，需要转换为 player.name
      // 因为服务端 executeReverseCard 用 p.name 查找目标玩家
      const targetPlayerName = targetId
        ? players.find(p => p.id === targetId)?.name
        : undefined;
      console.log('[useCard] sending game:use_card to server:', cardId, 'target:', targetPlayerName, 'extra:', extra);
      useRoomStore.getState().sendUseCardCommand(cardId, targetPlayerName, extra);
      return;
    }
    // 热座模式：本地执行卡牌效果
    const s = getState();
    const cp = s.players[s.currentPlayerIndex];
    if (!cp || cp.isBot) return;

    console.log('[useCard] hot-seat applying card:', cardId, 'target:', targetId, 'extra:', extra);
    try {
      applyReverseCard(s as any, useGameStore.setState, cp.id, cardId, targetId || null, extra);
      // applyReverseCard 内部会调用 addMoney/removeReverseCard 等 action（触发 Zustand set）
      // 但直接 mutation (state.players = ...) 不会触发渲染，需要手动同步
      useGameStore.setState({});
      setBroadcast(`${cp.name} 使用了 ${REVERSE_CARDS.find(c => c.id === cardId)?.emoji || ''} ${REVERSE_CARDS.find(c => c.id === cardId)?.name || cardId}`, 'info');
    } catch (err) {
      console.error('[useCard] error:', err);
      addToast('卡牌使用失败', 'error');
    }
  }, [isOnline, getState, setBroadcast, players]);

  // ====== Hover tooltip ======
  const hoverTooltip = useMemo(() => {
    if (!hoveredCell) return null;
    const { cell } = hoveredCell;
    const prop = properties.find(p => p.cellId === cell.id);
    const owner = prop ? players.find(p => p.id === prop.ownerId) : null;
    return (
      <div className={styles.cellTooltip}>
        <div className={styles.cellTooltipHeader}>
          <span className={styles.cellTooltipEmoji}>{cell.emoji || ''}</span>
          <span className={styles.cellTooltipName}>{cell.name}</span>
        </div>
        {cell.street && <div className={styles.cellTooltipRow}><span className={styles.cellTooltipLabel}>街区</span><span>{cell.street}</span></div>}
        {cell.type === 'property' && cell.price && cell.rentTable && (<>
          <div className={styles.cellTooltipRow}><span className={styles.cellTooltipLabel}>价格</span><span className={styles.cellTooltipGold}>¥{cell.price.toLocaleString()}</span></div>
          <div className={styles.cellTooltipRow}><span className={styles.cellTooltipLabel}>租金</span><span>空¥{cell.rentTable[0]} / 房¥{cell.rentTable[1]} / 墅¥{cell.rentTable[2]} / 酒¥{cell.rentTable[3]}</span></div>
        </>)}
        {cell.description && <div className={styles.cellTooltipDesc}>{cell.description}</div>}
        {owner && <div className={styles.cellTooltipOwner}>拥有者: {owner.name} (Lv.{prop!.buildingLevel})</div>}
      </div>
    );
  }, [hoveredCell, properties, players]);

  // ====== 渲染 ======
  if (phase === 'gameOver' && winnerId) {
    return (
      <div className={styles.container} ref={containerRef}>
        <canvas ref={canvasRef} className={styles.canvas} />
        <GameOverOverlay winnerId={winnerId} />
      </div>
    );
  }

  return (
    <>
      {/* 手机竖屏轻提示（非阻塞，自动消失）— 仅非全屏状态显示 */}
      {isMobile && isPortrait && !isFullscreenActive && !orientationDismissed && (
        <div className={styles.portraitHint}>
          <span className={styles.portraitHintIcon}>🔄</span>
          <span>横屏体验更佳</span>
          <button className={styles.portraitHintDismiss} onClick={() => setOrientationDismissed(true)}>×</button>
        </div>
      )}

      {/* ... all the overlays ... */}

      {/* ======== 全局浮动按钮（容器外部，避免 overflow 裁剪） ======== */}
      {/* 横竖屏切换按钮（移动端左上角） */}
      {isMobile && (
        <button
          className={styles.orientationBtn}
          onClick={handleToggleOrientation}
          title={isPortrait ? '切换为横屏' : '切换为竖屏'}
          aria-label="切换横竖屏"
        >
          {isPortrait ? '🔄' : '📱'}
        </button>
      )}

      {/* 全屏/非全屏切换按钮（移动端，横竖屏按钮右边） */}
      {isMobile && (
        <button
          className={styles.fullscreenBtn}
          onClick={handleToggleFullscreen}
          title={isFullscreenActive ? '退出全屏' : '进入全屏'}
          aria-label="切换全屏"
        >
          {isFullscreenActive ? '↙️' : '↗️'}
        </button>
      )}

      {/* 关闭按钮（右上角，返回首页） */}
      <button
        className={styles.closeBtn}
        onClick={() => setShowExitConfirm(true)}
        title="退出游戏"
        aria-label="退出游戏"
      >
        ✕
      </button>

      {/* 玩家列表浮层按钮（仅移动端横屏，竖屏面板常驻右侧） */}
      {isMobile && !isPortrait && !mobilePlayerListOpen && (
        <button
          className={styles.playerListFloatBtn}
          onClick={() => setMobilePlayerListOpen(true)}
        >
          👥 玩家 {players.length}
        </button>
      )}

      <div className={`${styles.container} ${isFullscreenPortrait ? styles.fullscreenPortrait : ''} ${isMobile && isPortrait && !isFullscreenActive ? styles.portraitLayout : ''}`} ref={containerRef}>
        <canvas ref={canvasRef} className={styles.canvas} /><ToastStack />

      {/* 全局事件广播 */}
      {broadcast && (
        <Broadcast
          message={broadcast.message}
          type={broadcast.type}
          onDone={handleBroadcastDone}
        />
      )}

      {/* 步进提示 */}
      {isMoving && currentStepCell && (
        <div className={styles.stepIndicator}>
          <span className={styles.stepIcon}>👣</span>
          <span className={styles.stepText}>经过 {currentStepCell}</span>
        </div>
      )}

      {/* 全局事件指示器 */}
      {activeGlobalEvent && (
        <div className={`${styles.globalEventBanner} ${activeGlobalEvent.event.id === 'economic_winter' ? styles.globalEventBannerWinter : styles.globalEventBannerGolden}`}>
          <div className={styles.globalEventIcon}>{activeGlobalEvent.event.emoji}</div>
          <div className={styles.globalEventContent}>
            <div className={styles.globalEventName}>{activeGlobalEvent.event.name}</div>
            <div className={styles.globalEventDesc}>{activeGlobalEvent.event.description}</div>
          </div>
          <div className={styles.globalEventTurns}>
            <span className={styles.globalEventTurnsNum}>{activeGlobalEvent.remainingTurns}</span>
            <span className={styles.globalEventTurnsLabel}>轮</span>
          </div>
        </div>
      )}

      {/* 棋盘 hover tooltip（移动端关闭，仅桌面端） */}
      {hoveredCell && !isMobile && (
        <div className={styles.cellHoverOverlay} style={{ left: hoveredCell.x + 16, top: hoveredCell.y }}>
          {hoverTooltip}
        </div>
      )}

      {/* 格子选中详情面板（棋盘中央） */}
      {selectedCell !== null && (() => {
        const cell = BOARD_CELLS[selectedCell];
        if (!cell) return null;
        const prop = properties.find(p => p.cellId === selectedCell);
        const owner = prop ? players.find(p => p.id === prop.ownerId) : null;
        return (
          <div className={styles.cellDetailOverlay} onClick={() => setSelectedCell(null)}>
            <div className={styles.cellDetailCard} onClick={e => e.stopPropagation()}>
              <div className={styles.cellDetailHeader}>
                <span className={styles.cellDetailEmoji}>{cell.emoji || '📍'}</span>
                <div className={styles.cellDetailTitle}>
                  <div className={styles.cellDetailName}>{cell.name}</div>
                  {cell.street && <div className={styles.cellDetailStreet}>{cell.street}</div>}
                </div>
                <button className={styles.cellDetailClose} onClick={() => setSelectedCell(null)}>✕</button>
              </div>
              <div className={styles.cellDetailBody}>
                {cell.type === 'property' && cell.price && cell.rentTable && (
                  <>
                    <div className={styles.cellDetailRow}>
                      <span className={styles.cellDetailLabel}>价格</span>
                      <span className={styles.cellDetailGold}>¥{cell.price.toLocaleString()}</span>
                    </div>
                    <div className={styles.cellDetailRow}>
                      <span className={styles.cellDetailLabel}>空地</span>
                      <span>¥{cell.rentTable[0]}</span>
                    </div>
                    <div className={styles.cellDetailRow}>
                      <span className={styles.cellDetailLabel}>房子</span>
                      <span>¥{cell.rentTable[1]}</span>
                    </div>
                    <div className={styles.cellDetailRow}>
                      <span className={styles.cellDetailLabel}>别墅</span>
                      <span>¥{cell.rentTable[2]}</span>
                    </div>
                    <div className={styles.cellDetailRow}>
                      <span className={styles.cellDetailLabel}>酒店</span>
                      <span>¥{cell.rentTable[3]}</span>
                    </div>
                    {/* 升级费用 */}
                    {cell.upgradeCost && (
                      <div className={styles.cellDetailRow}>
                        <span className={styles.cellDetailLabel}>升级费</span>
                        <span className={styles.cellDetailGold}>¥{cell.upgradeCost.toLocaleString()}</span>
                      </div>
                    )}
                  </>
                )}
                {cell.description && <div className={styles.cellDetailDesc}>{cell.description}</div>}
                {owner && (
                  <div className={styles.cellDetailOwner}>
                    拥有者: <span style={{ color: CHARACTERS.find(c => c.id === owner.characterId)?.color || '#fff', fontWeight: 700 }}>
                      {owner.name}
                    </span>
                    {prop!.buildingLevel > 0 && (
                      <span className={styles.cellDetailLevel}>
                        {' ⭐'.repeat(prop!.buildingLevel)} Lv.{prop!.buildingLevel}
                      </span>
                    )}
                  </div>
                )}
                {!owner && cell.type === 'property' && (
                  <div className={styles.cellDetailOwner} style={{ color: 'rgba(255,255,255,0.4)' }}>无主之地</div>
                )}

                {/* 已达最高等级提示 */}
                {cell.type === 'property' && owner && owner.id === currentPlayer?.id && prop!.buildingLevel >= 3 && (
                  <div className={styles.cellDetailUpgrade}>
                    <div className={styles.cellDetailUpgradeMax}>
                      ⭐⭐⭐ 已满级（酒店）
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* 右侧玩家列表 */}
      {/* PC：常显可折叠 / 移动端竖屏：右侧悬浮面板 / 移动端横屏：底部浮层 */}
      {((!isMobile || isPortrait) || mobilePlayerListOpen) && (
        <PlayerList
          players={players}
          currentPlayerIndex={currentPlayerIndex}
          board={board}
          properties={properties}
          selectedPlayerId={selectedPlayerId}
          onSelectPlayer={setSelectedPlayerId}
          collapsed={isMobile && isPortrait ? portraitPlayerListCollapsed : (isMobile ? false : playerListCollapsed)}
          onToggleCollapse={isMobile && isPortrait
            ? () => setPortraitPlayerListCollapsed(c => !c)
            : (isMobile ? () => setMobilePlayerListOpen(false) : () => setPlayerListCollapsed(c => !c))}
          isMobile={isMobile && !isPortrait}
          isPortraitMobile={isMobile && isPortrait}
        />
      )}

      {/* ======== 竖屏信息面板（棋盘下方，可滚动区域） ======== */}
      {isMobile && isPortrait && !isFullscreenActive && (() => {
        const seen = new Set<string>();
        const alliances: { a: typeof players[0]; b: typeof players[0]; turns: number }[] = [];
        players.forEach(p => {
          if (!p.allianceWith) return;
          const key = [p.id, p.allianceWith].sort().join('-');
          if (seen.has(key)) return;
          seen.add(key);
          const other = players.find(o => o.id === p.allianceWith);
          if (other) alliances.push({ a: p, b: other, turns: p.allianceTurns });
        });
        return (
          <div className={styles.portraitInfoPanel}>
            {/* 日志（最近 5 条） */}
            {logs.length > 0 && (
              <div className={styles.logPanel}>
                {logs.slice(-5).map((log, i) => {
                  const p = players.find(x => x.id === log.playerId);
                  return (
                    <div key={i} className={styles.logItem}>
                      <span className={styles.logTurn}>[{log.turn}]</span>
                      {p && <span className={styles.logPlayer}>{p.name}: </span>}
                      <span className={styles.logMsg}>{log.message}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {/* 联盟关系 */}
            {alliances.length > 0 && (
              <div className={styles.alliancePanel}>
                <div className={styles.allianceTitle}>🤝 联盟关系</div>
                {alliances.map((al, i) => (
                  <div key={i} className={styles.allianceItem}>
                    <span style={{ color: CHARACTERS.find(c => c.id === al.a.characterId)?.color || '#fff' }}>{al.a.name}</span>
                    <span className={styles.allianceSymbol}>⇔</span>
                    <span style={{ color: CHARACTERS.find(c => c.id === al.b.characterId)?.color || '#fff' }}>{al.b.name}</span>
                    <span className={styles.allianceTurns}>{al.turns}回合</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ======== 竖屏全屏模式：浮动日志面板（覆盖在画布上方） ======== */}
      {isMobile && isPortrait && isFullscreenActive && logs.length > 0 && (
        <div className={styles.portraitFloatingLog}>
          {logs.slice(-4).map((log, i) => {
            const p = players.find(x => x.id === log.playerId);
            return (
              <div key={i} className={styles.logItem}>
                <span className={styles.logTurn}>[{log.turn}]</span>
                {p && <span className={styles.logPlayer}>{p.name}: </span>}
                <span className={styles.logMsg}>{log.message}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* 底部操作区 */}
      <div className={styles.bottomBar}>
        <DiceRoller
          diceResult={diceResult}
          turnPhase={turnPhase}
          currentPlayer={currentPlayer}
          currentChar={currentChar}
          moveSteps={moveSteps}
          onRoll={doRoll}
          isMyTurn={isMyTurn}
          isWaitingForOthers={isOnline && !isMyTurn && currentPlayer && !currentPlayer.isBot}
          countdown={diceCountdown}
        />
        <div className={styles.turnInfo}>
          {isOnline && roomId && (
            <div className={styles.roomBadge}>
              <span className={styles.roomBadgeIcon}>🏠</span>
              <span className={styles.roomBadgeCode}>{roomId}</span>
            </div>
          )}
          <div className={styles.turnLabel}>第 {roundNumber} 轮 · {currentPlayer?.name || '...'} 的回合</div>
          {/* 联机等待提示：非本地玩家回合 */}
          {isOnline && !isMyTurn && currentPlayer && !currentPlayer.isBot && (
            <div className={styles.waitingIndicator}>
              <span className={styles.waitingIndicatorIcon}>⏳</span>
              <span className={styles.waitingIndicatorText}>
                {turnPhase === 'moving'
                  ? `${currentPlayer.name} 正在移动棋子...`
                  : `等待 ${currentPlayer.name} 操作中...`}
              </span>
            </div>
          )}
          {currentChar && (
            <Tooltip position="top" width={220} content={<>
              <span className="tooltipTitle">{currentChar.emoji} {currentChar.name} · {currentChar.passiveName}</span>
              <span className="tooltipDesc">{currentChar.passiveDesc}</span>
            </>}>
              <div className={styles.charInfo}>
                <span className={styles.charEmoji}>{currentChar.emoji}</span>
                <span className={styles.charPassive}>{currentChar.passiveName}</span>
              </div>
            </Tooltip>
          )}
          {/* 刷新按钮：卡住时手动同步最新状态 */}
          {isOnline && (
            <button className={styles.refreshBtn} onClick={() => {
              console.log('[refresh] 手动请求刷新状态');
              useRoomStore.getState().sendRefreshStateCommand();
              addToast('正在刷新游戏状态...', 'info');
            }} title="刷新游戏状态">
              🔄 刷新
            </button>
          )}
          {currentPlayer?.isBot && botCountdown > 0 && (
            <div className={styles.botCountdown}>
              <span className={styles.botCountdownLabel}>AI 思考中</span>
              <span className={styles.botCountdownNum}>{botCountdown}s</span>
            </div>
          )}
        </div>
        <div className={styles.actionButtons}>
          {identityPlayer && !identityPlayer.isBot && !identityPlayer.identityRevealed && currentIdentity && (
            <Tooltip position="top" width={260} content={<>
              <span className="tooltipTitle">{currentIdentity.emoji} {currentIdentity.name}</span>
              <span className="tooltipDesc">隐藏：{currentIdentity.beforeRevealDesc}</span>
              <span className="tooltipEffect">暴露后：{currentIdentity.afterRevealDesc}</span>
            </>}>
              <button className={styles.identityBtn} onClick={() => setShowIdentityReveal(true)}
                disabled={identityPlayer.identityRevealed}>
                🃏 暴露身份
              </button>
            </Tooltip>
          )}
          {identityPlayer && !identityPlayer.isBot && identityPlayer.identityRevealed && currentIdentity && (
            <Tooltip position="top" width={240} content={<>
              <span className="tooltipTitle">{currentIdentity.emoji} {currentIdentity.name} · 已暴露</span>
              <span className="tooltipEffect">{currentIdentity.revealEffect}</span>
            </>}>
              <span className={styles.identityRevealed}>{currentIdentity.emoji} {currentIdentity.name}</span>
            </Tooltip>
          )}
          {/* 购买决策由 BuyDialog 处理（含自动倒计时 + "不购买"按钮），此处不再显示冗余跳过按钮 */}
        </div>
      </div>

      {/* 手牌区 — 联机模式显示本地玩家的手牌，热座模式显示当前回合玩家的手牌 */}
      {(() => {
        const cardPlayer = isOnline ? localPlayer : currentPlayer;
        if (!cardPlayer || cardPlayer.isBot || cardPlayer.bankrupt) return null;
        return (
          <CardHand player={cardPlayer} onUseCard={handleUseCard} players={players} board={board} turnPhase={turnPhase} isMobile={isMobile} isPortrait={isMobile && isPortrait} isMyTurn={isMyTurn} />
        );
      })()}

      {/* 对话框 */}
      {showBuyDialog && currentPlayer && (
        <BuyDialog cell={board[currentPlayer.position]} player={currentPlayer} onConfirm={handleBuyConfirm} />
      )}
      {upgradeOffer && currentPlayer && (
        <UpgradeOffer
          cell={upgradeOffer.cell}
          player={currentPlayer}
          currentLevel={upgradeOffer.prop.buildingLevel || 0}
          upgradeCost={upgradeOffer.cell.upgradeCost || 0}
          currentRent={upgradeOffer.cell.rentTable?.[upgradeOffer.prop.buildingLevel || 0] || 0}
          newRent={upgradeOffer.cell.rentTable?.[(upgradeOffer.prop.buildingLevel || 0) + 1] || 0}
          onUpgrade={handleUpgradeConfirm}
          onSkip={handleUpgradeSkip}
        />
      )}
      {jailChoice && currentPlayer && (
        <JailDialog
          player={currentPlayer}
          onPayBail={handleJailPayBail}
          onTryDoubles={handleJailTryDoubles}
        />
      )}
      {showReverseShop && currentPlayer && (
        <ReverseShop onClose={handleReverseShopClose} />
      )}
      {showEventModal && (
        <EventModal type={showEventModal.type} data={showEventModal.data} onClose={handleEventClose} />
      )}
      {showIdentityReveal && identityPlayer && currentIdentity && (
        <IdentityReveal identity={currentIdentity} player={identityPlayer}
          onConfirm={handleRevealIdentity} onCancel={() => setShowIdentityReveal(false)} />
      )}

      {/* 破产清算 */}
      {showBankruptcyLiquidation && pendingBankruptPlayerId && (
        <BankruptcyLiquidation />
      )}

      {/* 角色技能：鹰眼投资人 命运卡预览 */}
      <FatePreview isPortrait={isMobile && isPortrait} />

      {/* 角色技能：兔子赌徒 双骰抉择 */}
      <DiceChoice />

      {/* 角色技能：猫头鹰学者 买地前预览 */}
      {showOwlPreview && owlCellInfo && (
        <OwlPreview
          cellId={owlCellInfo.cellId}
          cellName={owlCellInfo.cellName}
          cellPrice={owlCellInfo.cellPrice}
          onDecide={handleOwlDecide}
        />
      )}

      {/* 联盟关系标识 — 移动端隐藏 */}
      {!isMobile && (() => {
        const seen = new Set<string>();
        const alliances: { a: typeof players[0]; b: typeof players[0]; turns: number }[] = [];
        players.forEach(p => {
          if (!p.allianceWith) return;
          const key = [p.id, p.allianceWith].sort().join('-');
          if (seen.has(key)) return;
          seen.add(key);
          const other = players.find(o => o.id === p.allianceWith);
          if (other) alliances.push({ a: p, b: other, turns: p.allianceTurns });
        });
        if (alliances.length === 0) return null;
        return (
          <div className={styles.alliancePanel}>
            <div className={styles.allianceTitle}>🤝 联盟关系</div>
            {alliances.map((al, i) => (
              <div key={i} className={styles.allianceItem}>
                <span style={{ color: CHARACTERS.find(c => c.id === al.a.characterId)?.color || '#fff' }}>{al.a.name}</span>
                <span className={styles.allianceSymbol}>⇔</span>
                <span style={{ color: CHARACTERS.find(c => c.id === al.b.characterId)?.color || '#fff' }}>{al.b.name}</span>
                <span className={styles.allianceTurns}>{al.turns}回合</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* 日志 — 移动端隐藏 */}
      {!isMobile && (
        <div className={styles.logPanel}>
          {logs.slice(-6).map((log, i) => {
          const p = players.find(x => x.id === log.playerId);
          return (
            <div key={i} className={styles.logItem}>
              <span className={styles.logTurn}>[{log.turn}]</span>
              {p && <span className={styles.logPlayer}>{p.name}: </span>}
              <span className={styles.logMsg}>{log.message}</span>
            </div>
          );
        })}
      </div>
      )}

      {/* ======== 退出确认对话框 ======== */}
      {showExitConfirm && (
        <div className={styles.exitOverlay} onClick={() => setShowExitConfirm(false)}>
          <div className={styles.exitDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.exitIcon}>🚪</div>
            <div className={styles.exitTitle}>退出游戏</div>
            <div className={styles.exitDesc}>确定要退出当前游戏吗？<br />进度不会保存。</div>
            <div className={styles.exitActions}>
              <button className={styles.exitCancelBtn} onClick={() => setShowExitConfirm(false)}>继续游戏</button>
              <button className={styles.exitConfirmBtn} onClick={handleExitGame}>退出</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
